import { useEffect, useState } from "react";
import { CheckCircle2, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { InterestForm } from "@/components/InterestForm";
import { ServiceRequestForm } from "@/components/ServiceRequestForm";
import { FadeIn } from "@/components/Reveal";

const PLANNED = [
  { title: "Employer screening programs", desc: "Support for employer drug testing programs and workplace readiness." },
  { title: "Workplace readiness focus", desc: "Designed for businesses building safer, well-documented workforce practices." },
  { title: "Coordinated with TSM services", desc: "Planned to connect with background screening and workforce verification support." },
];

export default function DrugTestingPage() {
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    document.title = "TSM Drug Testing | Coming Soon | TSM Group CLE";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Workforce drug testing services designed to support employer screening programs and workplace readiness. Join the interest list to hear when TSM Drug Testing launches.");
    api.get("/api/tsm4/status").then((r) => setStatus(r.data.drug_testing)).catch(() => setStatus("coming_soon"));
  }, []);

  return (
    <div className="bg-paper" data-testid="drug-testing-page">
      <PageHero
        testid="drug-testing-hero"
        eyebrow="TSM4 — In Preparation"
        title={<>TSM Drug Testing <span className="text-gold">| Coming Soon</span></>}
        copy="Workforce drug testing services designed to support employer screening programs and workplace readiness. This service is not yet operational — join the interest list and we'll contact you when it launches."
      >
        <div className="mt-9">
          <span className="inline-flex items-center gap-2 rounded-full border border-dashed border-teal/60 bg-teal/10 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-teal" data-testid="drug-testing-status-chip">
            <Clock className="h-3.5 w-3.5" />
            Coming Soon — Not Currently Operational
          </span>
        </div>
      </PageHero>

      <section className="py-24 lg:py-32">
        <div className="container-tsm max-w-[1200px]">
          <FadeIn className="max-w-3xl">
            <p className="eyebrow">What We're Preparing</p>
            <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl" data-testid="drug-testing-planned-headline">
              Built to fit the way you hire.
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-5 sm:grid-cols-3" data-testid="drug-testing-planned-grid">
            {PLANNED.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.07} className="h-full">
                <div className="h-full rounded-xl border border-navy/10 bg-white p-7">
                  <CheckCircle2 className="h-5 w-5 text-teal" />
                  <h3 className="mt-4 text-lg font-extrabold text-navy">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/65">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.15}>
            <p className="mt-10 max-w-3xl text-sm italic leading-relaxed text-ink/55">
              Service details will be announced when TSM Drug Testing becomes operational. No launch date has been
              confirmed yet.
            </p>
          </FadeIn>
        </div>
      </section>

      {status === "available" ? (
        <ServiceRequestForm defaultService="Drug Testing" />
      ) : (
        <InterestForm
          interestType="drug_testing"
          heading="Join the TSM Drug Testing interest list."
          intro="Tell us a little about your business and what you anticipate needing. When TSM Drug Testing launches, you'll be among the first to know."
          submitLabel="Join the Interest List"
          testidPrefix="drug-testing-interest"
        />
      )}
    </div>
  );
}
